/**
 * Email Service Utility
 * 
 * This module provides functionality for sending emails using Resend API.
 * It encapsulates all email-related operations and error handling.
 */

import { Resend } from 'resend';

// Environment variables should be properly set up in a .env file
// and accessed using a secure method in production
const RESEND_API_KEY = import.meta.env.VITE_RESEND_API_KEY || '';

/**
 * Initialize Resend instance with API key
 * API key should be provided via environment variables for security
 * 
 * Note: You need to add your Resend API key to .env.local:
 * VITE_RESEND_API_KEY=re_your_api_key_here
 */
let resend: Resend;

// Only initialize if we have an API key
try {
  // Check if we have a valid API key
  if (!RESEND_API_KEY || RESEND_API_KEY === '' || RESEND_API_KEY.startsWith('re_dummy')) {
    console.warn('⚠️ No valid Resend API key found. Email functionality will be disabled. ' +
                 'Add VITE_RESEND_API_KEY to your .env.local file.');
    // Initialize with a placeholder to prevent errors
    resend = new Resend('re_placeholder');
  } else {
    // Initialize with the actual API key
    resend = new Resend(RESEND_API_KEY);
  }
} catch (error) {
  console.error('Failed to initialize Resend:', error);
  // Initialize with a placeholder to prevent errors
  resend = new Resend('re_placeholder');
}

/**
 * Interface for email sending options
 */
export interface EmailOptions {
  to: string | string[];
  subject: string;
  html?: string;
  text?: string;
  from?: string;
  replyTo?: string;
  attachments?: Array<{
    filename: string;
    content: Buffer | string;
  }>;
}

/**
 * Send an email using Resend API
 * @param options Email configuration options
 * @returns Promise containing the send result
 */
export async function sendEmail(options: EmailOptions) {
  try {
    // Validate API key
    if (!RESEND_API_KEY || RESEND_API_KEY === '' || RESEND_API_KEY.startsWith('re_dummy')) {
      return {
        success: false,
        error: 'Resend API key is not properly configured. Please add your API key as VITE_RESEND_API_KEY in .env.local'
      };
    }

    // Set default from address if not provided
    const fromAddress = options.from || 'notifications@meduler.app';

    // Send the email
    const { data, error } = await resend.emails.send({
      from: fromAddress,
      to: Array.isArray(options.to) ? options.to : [options.to],
      subject: options.subject,
      html: options.html,
      text: options.text || '',
      replyTo: options.replyTo,
      attachments: options.attachments,
    });

    if (error) {
      console.error('Failed to send email:', error);
      throw new Error(`Failed to send email: ${error.message}`);
    }

    return { success: true, data };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error sending email';
    console.error('Email sending error:', errorMessage);
    return { success: false, error: errorMessage };
  }
}

/**
 * Send a test email to verify Resend API configuration
 * @param recipient Email address to send the test to
 * @returns Promise with the result of the test
 */
export async function sendTestEmail(recipient: string) {
  const currentDate = new Date().toLocaleString();
  
  return sendEmail({
    to: recipient,
    subject: 'Meduler Test Email',
    html: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #eaeaea; border-radius: 5px;">
        <h2 style="color: #4a5568;">Meduler Email Test</h2>
        <p>This is a test email from Meduler to confirm that email sending is working correctly.</p>
        <p>Test sent at: ${currentDate}</p>
        <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #eaeaea; color: #718096; font-size: 12px;">
          <p>This is an automated message from Meduler. Please do not reply to this email.</p>
        </div>
      </div>
    `,
    text: `Meduler Email Test\n\nThis is a test email from Meduler to confirm that email sending is working correctly.\n\nTest sent at: ${currentDate}\n\nThis is an automated message from Meduler. Please do not reply to this email.`,
  });
}
